import { useEffect, useState } from "react"
import { csrfToken } from "@/lib/csrf"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  PRIVACY_LABELS,
  type TiktokCreatorInfo,
  type TiktokPostState,
  type TiktokPrivacyLevel,
} from "@/lib/tiktok-post-state"

type FetchState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; info: TiktokCreatorInfo }

export default function TiktokPostOptions({
  connectionId,
  state,
  onChange,
}: {
  connectionId: number | string
  state: TiktokPostState
  onChange: (next: TiktokPostState) => void
}) {
  const [fetchState, setFetchState] = useState<FetchState>({ status: "loading" })

  useEffect(() => {
    let cancelled = false

    fetch(`/dashboard/connections/${connectionId}/tiktok_creator_info`, {
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      credentials: "same-origin",
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body.error || `Request failed (${res.status})`)
        }
        return res.json() as Promise<TiktokCreatorInfo>
      })
      .then((info) => {
        if (cancelled) return
        setFetchState({ status: "ready", info })
      })
      .catch((err: Error) => {
        if (cancelled) return
        setFetchState({ status: "error", message: err.message })
      })

    return () => {
      cancelled = true
    }
  }, [connectionId])

  function update(patch: Partial<TiktokPostState>) {
    onChange({ ...state, ...patch })
  }

  function toggleBrandedContent(next: boolean) {
    const patch: Partial<TiktokPostState> = { brandedContent: next }
    if (next && state.privacy === "SELF_ONLY") {
      patch.privacy = ""
    }
    update(patch)
  }

  if (fetchState.status === "loading") {
    return (
      <div className="mt-3 rounded-lg border border-stone/30 bg-stone/5 px-3 py-3 text-sm text-warm-grey">
        Loading TikTok account details…
      </div>
    )
  }

  if (fetchState.status === "error") {
    return (
      <div className="mt-3 rounded-lg border border-red/30 bg-red/5 px-3 py-3 text-sm text-red">
        {fetchState.message} Try reconnecting your TikTok account.
      </div>
    )
  }

  const info = fetchState.info

  const musicLink = (
    <a
      href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en"
      target="_blank"
      rel="noopener noreferrer"
      className="underline decoration-stone hover:text-charcoal"
    >
      Music Usage Confirmation
    </a>
  )
  const brandedLink = (
    <a
      href="https://www.tiktok.com/legal/page/global/bc-policy/en"
      target="_blank"
      rel="noopener noreferrer"
      className="underline decoration-stone hover:text-charcoal"
    >
      Branded Content Policy
    </a>
  )

  const attestation =
    state.commercialContent && state.brandedContent ? (
      <>By posting, you agree to TikTok&apos;s {brandedLink} and {musicLink}.</>
    ) : (
      <>By posting, you agree to TikTok&apos;s {musicLink}.</>
    )

  return (
    <div className="mt-3 space-y-4">
      {/* Creator strip */}
      <div className="flex items-center gap-3 rounded-lg border border-stone/40 bg-stone/5 px-3 py-2.5">
        {info.creator_avatar_url ? (
          <img
            src={info.creator_avatar_url}
            alt=""
            className="size-8 rounded-full object-cover"
          />
        ) : (
          <div className="size-8 rounded-full bg-stone/40" />
        )}
        <div className="min-w-0 text-sm">
          <p className="font-medium text-charcoal">
            {info.creator_nickname || "TikTok creator"}
          </p>
          {info.creator_username && (
            <p className="truncate text-xs text-warm-grey">@{info.creator_username}</p>
          )}
        </div>
      </div>

      {/* Privacy */}
      <div className="space-y-1.5">
        <Label htmlFor="tiktok-privacy" className="text-[11px] font-medium uppercase tracking-widest text-warm-grey">
          Who can watch this video
        </Label>
        <Select
          value={state.privacy || undefined}
          onValueChange={(v) => update({ privacy: v as TiktokPrivacyLevel })}
        >
          <SelectTrigger id="tiktok-privacy" className="w-full">
            <SelectValue placeholder="Select audience" />
          </SelectTrigger>
          <SelectContent>
            {info.privacy_level_options.map((opt) => {
              const disabled = state.brandedContent && opt === "SELF_ONLY"
              return (
                <SelectItem key={opt} value={opt} disabled={disabled}>
                  {PRIVACY_LABELS[opt] ?? opt}
                </SelectItem>
              )
            })}
          </SelectContent>
        </Select>
        {state.brandedContent && (
          <p className="text-[11px] text-warm-grey">
            Branded content visibility cannot be set to private.
          </p>
        )}
      </div>

      {/* Allow interactions */}
      <div className="space-y-2">
        <p className="text-[11px] font-medium uppercase tracking-widest text-warm-grey">
          Allow viewers to
        </p>
        <div className="space-y-3 rounded-lg border border-stone/40 px-3 py-3">
          <ToggleRow
            id="tiktok-allow-comment"
            title="Comment"
            description="Viewers can leave comments on your video."
            checked={state.allowComments}
            disabled={info.comment_disabled}
            onChange={(v) => update({ allowComments: v })}
          />
          <ToggleRow
            id="tiktok-allow-duet"
            title="Duet"
            description="Others can post a side-by-side video with yours."
            checked={state.allowDuet}
            disabled={info.duet_disabled}
            onChange={(v) => update({ allowDuet: v })}
          />
          <ToggleRow
            id="tiktok-allow-stitch"
            title="Stitch"
            description="Others can clip a section of your video and build on it."
            checked={state.allowStitch}
            disabled={info.stitch_disabled}
            onChange={(v) => update({ allowStitch: v })}
          />
        </div>
      </div>

      {/* Commercial content disclosure */}
      <div className="space-y-3 rounded-lg border border-stone/40 px-3 py-3">
        <ToggleRow
          id="tiktok-commercial"
          title="Disclose video content"
          description="Turn on if this video promotes a brand, product, or service."
          checked={state.commercialContent}
          onChange={(next) => {
            const patch: Partial<TiktokPostState> = { commercialContent: next }
            if (!next) {
              patch.yourBrand = false
              patch.brandedContent = false
            }
            update(patch)
          }}
        />

        {state.commercialContent && (
          <div className="space-y-3 border-t border-stone/30 pt-3">
            <ToggleRow
              id="tiktok-your-brand"
              title="Your brand"
              description={"Promoting yourself or your business. Labelled “Promotional content”."}
              checked={state.yourBrand}
              onChange={(v) => update({ yourBrand: v })}
            />
            <ToggleRow
              id="tiktok-branded"
              title="Branded content"
              description={"Promoting another brand or third party. Labelled “Paid partnership”."}
              checked={state.brandedContent}
              onChange={toggleBrandedContent}
            />
            {!state.yourBrand && !state.brandedContent && (
              <p className="text-[11px] text-red">
                Indicate if your content promotes yourself, a third party, or both.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Attestation */}
      <p className="text-[11px] leading-relaxed text-warm-grey">{attestation}</p>

      {info.max_video_post_duration_sec && (
        <p className="text-[11px] text-warm-grey">
          Max video length on this account: {info.max_video_post_duration_sec}s.
        </p>
      )}
    </div>
  )
}

function ToggleRow({
  id,
  title,
  description,
  checked,
  disabled = false,
  onChange,
}: {
  id: string
  title: string
  description: string
  checked: boolean
  disabled?: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <div
      className="flex items-start gap-3"
      title={disabled ? "Disabled in your TikTok account settings" : undefined}
    >
      <Switch
        id={id}
        checked={checked && !disabled}
        disabled={disabled}
        onCheckedChange={onChange}
        className="mt-0.5 shrink-0"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <Label
          htmlFor={id}
          className={`block cursor-pointer text-sm font-medium leading-tight ${
            disabled ? "text-warm-grey/60" : "text-charcoal"
          }`}
        >
          {title}
        </Label>
        <p className="text-[11px] leading-snug text-warm-grey">{description}</p>
      </div>
    </div>
  )
}
